import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, Users, Eye, Globe, Clock, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnalyticsDashboard = () => {
  const { user, isAdmin } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      fetchAnalytics();
      // Refresh every 30 seconds
      const interval = setInterval(fetchAnalytics, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API}/analytics/summary`, {
        withCredentials: true
      });
      setAnalytics(data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen pt-20 bg-ewss-navy flex items-center justify-center">
        <div className="text-white text-xl">Loading analytics...</div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color = "text-ewss-cyan" }) => (
    <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
      <div className="flex items-center justify-between mb-2">
        <Icon className={`${color}`} size={24} />
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <div className="text-3xl font-bold text-white">{value.toLocaleString()}</div>
    </div>
  );

  return (
    <div className="min-h-screen pt-20 bg-ewss-navy">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Analytics Dashboard</h1>
          <p className="text-gray-400">Real-time website traffic and visitor insights</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Eye}
            label="Total Page Views"
            value={analytics.total_views}
            color="text-ewss-cyan"
          />
          <StatCard
            icon={Users}
            label="Unique Visitors"
            value={analytics.unique_visitors}
            color="text-green-400"
          />
          <StatCard
            icon={TrendingUp}
            label="Views Today"
            value={analytics.views_today}
            color="text-yellow-400"
          />
          <StatCard
            icon={Clock}
            label="Views This Week"
            value={analytics.views_this_week}
            color="text-purple-400"
          />
        </div>

        {/* Top Pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="text-ewss-cyan" size={24} />
              <h2 className="text-xl font-bold text-white">Top Pages</h2>
            </div>
            <div className="space-y-3">
              {analytics.top_pages.slice(0, 8).map((page, index) => (
                <div key={index} className="flex justify-between items-center">
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{page.title}</p>
                    <p className="text-gray-400 text-sm truncate">{page.url}</p>
                  </div>
                  <div className="ml-4 px-3 py-1 bg-ewss-cyan/20 rounded-full">
                    <span className="text-ewss-cyan font-semibold">{page.views}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Views by Country */}
          <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="text-ewss-cyan" size={24} />
              <h2 className="text-xl font-bold text-white">Top Countries</h2>
            </div>
            <div className="space-y-3">
              {analytics.views_by_country.length > 0 ? (
                analytics.views_by_country.map((country, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-white font-medium">{country.country || 'Unknown'}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-ewss-cyan"
                          style={{
                            width: `${(country.views / analytics.views_by_country[0].views) * 100}%`
                          }}
                        />
                      </div>
                      <span className="text-gray-400 w-12 text-right">{country.views}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-4">No country data available yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="text-ewss-cyan" size={24} />
            <h2 className="text-xl font-bold text-white">Recent Page Views</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700 text-left">
                  <th className="pb-3 text-gray-400 font-medium">Page</th>
                  <th className="pb-3 text-gray-400 font-medium">Country</th>
                  <th className="pb-3 text-gray-400 font-medium">Time</th>
                </tr>
              </thead>
              <tbody>
                {analytics.recent_views.slice(0, 15).map((view, index) => (
                  <tr key={index} className="border-b border-gray-700/50">
                    <td className="py-3">
                      <div className="text-white font-medium">{view.page_title}</div>
                      <div className="text-gray-400 text-sm">{view.page_url}</div>
                    </td>
                    <td className="py-3 text-gray-300">{view.country || '-'}</td>
                    <td className="py-3 text-gray-400 text-sm">
                      {new Date(view.timestamp).toLocaleString('en-ZA', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-8 bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
          <h3 className="text-xl font-bold text-white mb-4">Performance Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <p className="text-gray-400 mb-2">This Month</p>
              <p className="text-3xl font-bold text-white">{analytics.views_this_month.toLocaleString()}</p>
              <p className="text-sm text-gray-500">page views</p>
            </div>
            <div>
              <p className="text-gray-400 mb-2">Average per Day</p>
              <p className="text-3xl font-bold text-white">
                {Math.round(analytics.views_this_month / 30).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">page views</p>
            </div>
            <div>
              <p className="text-gray-400 mb-2">Pages per Visitor</p>
              <p className="text-3xl font-bold text-white">
                {analytics.unique_visitors > 0
                  ? (analytics.total_views / analytics.unique_visitors).toFixed(1)
                  : '0'}
              </p>
              <p className="text-sm text-gray-500">average</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
